// src/budget/model/program/programBudgetSnapshot.js

import React from "react";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";
import { formatCurrency, formatPercent } from "../../../shared/utils/formatters.js";

/**
 * @typedef {Object} FieldDef
 * @property {string} key
 *   Canonical field key used for value lookup on year rows (e.g. "teacherSalaries").
 * @property {string} label
 *   Human-friendly label shown in the snapshot table.
 * @property {("currency"|"number"|"percent"|string)} [valueType]
 *   Formatting intent. "currency" drives totals/subtotals and currency formatting.
 * @property {string} [group]
 *   Logical grouping label used to segment the snapshot into sections
 *   (e.g. "salaries", "positions", "materials", "offsets", "other").
 */

/**
 * @typedef {Object} ProgramYearRow
 * A single row object for a given fiscal start year, shaped by upstream row builders.
 *
 * Requirements (best-effort):
 * - Must include `year: number`
 * - Values accessed by `FieldDef.key` are expected to be numeric (or null/undefined)
 * - May include offset-rule metadata that controls totals inclusion and notes:
 *   `_offsetRules | OffsetRules | offsetRules`
 *
 * @property {number} year Fiscal start year.
 * @property {Object<string, any>} [any] Field-value bag.
 * @property {Object<string, OffsetRule>|Object<string, any>} [_offsetRules]
 * @property {Object<string, OffsetRule>|Object<string, any>} [OffsetRules]
 * @property {Object<string, OffsetRule>|Object<string, any>} [offsetRules]
 */

/**
 * @typedef {Object} OffsetRule
 * @property {boolean} [countInTotals]
 *   If false, this field's value is excluded from subtotal/total aggregations.
 * @property {string} [note]
 *   Optional explanatory note shown in snapshotNotes.
 */

/**
 * @typedef {Object} SnapshotColumn
 * Column definition object consumed by `CommonTable`.
 *
 * @property {string} key Unique column key.
 * @property {string|JSX.Element} label Header content.
 * @property {{width?: string, minWidth?: string}} [style] Column sizing.
 * @property {"left"|"right"|"center"} [align] Cell alignment.
 * @property {boolean} [editable] Whether column is editable (snapshot is non-editable).
 * @property {(row: SnapshotRow) => JSX.Element} [render] Optional cell renderer.
 */

/**
 * @typedef {Object} SnapshotRow
 * Row objects consumed by `CommonTable`.
 *
 * Row shapes vary by purpose:
 * - section header rows: `{ section: string }`
 * - detail rows: include `rowType: "detail"`, `label`, yearly value keys, change, percentChange
 * - subtotal rows: `rowType: "subtotal"`
 * - total rows: `rowType: "total"`, `isTotalRow: true`
 *
 * @property {"detail"|"subtotal"|"total"} [rowType]
 * @property {boolean} [isTotalRow]
 * @property {string} [label]
 * @property {string} [section]
 * @property {string} [valueType]
 * @property {string} [group]
 * @property {number} [order]
 *
 * @property {number|null} [y3Actual]
 * @property {number|null} [y2Actual]
 * @property {number|null} [y1Approved]
 * @property {number|null} [y1Actual]
 * @property {number|null} [y0Approved]
 *
 * @property {number|null} [change]
 * @property {number|null} [percentChange]
 */

/**
 * @typedef {Object} SnapshotNote
 * @property {string} key Normalized rule key (best-effort).
 * @property {string} note Human-friendly note explaining exclusion behavior.
 */

/**
 * @typedef {Object} BuildSnapshotTableParams
 * @property {number} snapshotYear
 *   Fiscal start year for the "current" column of the snapshot window.
 * @property {number[]} availableYears
 *   Valid fiscal years available for selection (used to validate snapshotYear).
 * @property {FieldDef[]} fieldDefs
 *   Field definitions used for labeling, grouping, and formatting.
 * @property {Array<Object>} tables
 *   Program table configs used to determine canonical field ordering.
 *   Snapshot ordering prefers `tables[].fields` from the "preferred" table.
 * @property {ProgramYearRow[]} approvedRows
 * @property {ProgramYearRow[]} actualRows
 * @property {ProgramYearRow[]} [proposedRows]
 * @property {Map<string, FieldDef>} fieldDefMap
 *   Map of field keys to field definitions (used to enforce valid ordering).
 * @property {string} [programLabel]
 *   Optional label to append to TOTAL row (e.g., "TOTAL SCIENCE").
 */

/**
 * buildSnapshotTable
 *
 * Builds a "Snapshot by Year" (PDF-style) table: a condensed, multi-year view
 * with grouped sections, derived Change / % Change columns, optional Salary subtotals,
 * and a final Program TOTAL row.
 *
 * Mode detection:
 * - Normal mode (typical):        Y-2 Actual, Y-1 Approved, Y-1 Actual, Y0 Approved/Proposed
 *   Baseline for change/pct:      Y-1 Actual
 * - Proposed-only current year:   Y-3 Actual, Y-2 Actual, Y-1 Approved, Y0 Proposed
 *   Baseline for change/pct:      Y-1 Approved
 * - Forward Approved-only mode:   same 4-col structure as proposed-only, but Y0 is Approved
 *   (used when actuals lag and current approved exists but prior-year actual missing)
 *
 * Offset rules:
 * - Row objects may include per-field rules indicating whether values are counted in totals.
 * - Excluded values are still displayed, but:
 *   - They are marked with an asterisk in the label
 *   - They are not included in subtotals/totals
 *   - Notes are deduped and returned via `snapshotNotes`
 *
 * @param {BuildSnapshotTableParams} params
 * @returns {{
 *   snapshotColumns: SnapshotColumn[],
 *   snapshotRows: SnapshotRow[],
 *   snapshotNotes: SnapshotNote[]
 * }}
 */
export function buildSnapshotTable({
  snapshotYear,
  availableYears,
  fieldDefs,
  tables,
  approvedRows,
  actualRows,
  proposedRows = [],
  fieldDefMap,
  programLabel,
}) {
  if (
    !snapshotYear ||
    !availableYears?.length ||
    !fieldDefs?.length ||
    !tables?.length
  ) {
    return { snapshotColumns: [], snapshotRows: [], snapshotNotes: [] };
  }

  const current = snapshotYear;
  const prev1 = current - 1;
  const prev2 = current - 2;
  const prev3 = current - 3;

  let orderCounter = 0;

  const approvedByYear = new Map((approvedRows || []).map((r) => [r.year, r]));
  const actualByYear = new Map((actualRows || []).map((r) => [r.year, r]));
  const proposedByYear = new Map((proposedRows || []).map((r) => [r.year, r]));

  const orderedFieldKeys = getCanonicalFieldOrder(tables, fieldDefs).filter((k) =>
    fieldDefMap?.has?.(k)
  );

  const orderedFieldDefs = orderedFieldKeys
    .map((k) => fieldDefMap.get(k))
    .filter(Boolean);

  // ---------------------------------------------------------------------------
  // Mode detection
  // ---------------------------------------------------------------------------

  const hasApprovedCurrent = approvedByYear.has(current);
  const hasProposedCurrent = proposedByYear.has(current);
  const hasActualPrev1 = actualByYear.has(prev1);

  // Mode A: Proposed-only current year (BoE classic)
  const isCurrentYearProposedOnly = !hasApprovedCurrent && hasProposedCurrent;

  // Mode B: Forward Approved-only (Town case)
  // current has Approved, but prior-year Actual is missing (common when actuals lag).
  // Use the same 4-col structure as Proposed-only, but current column is Approved.
  const isForwardApprovedOnly =
    hasApprovedCurrent &&
    !hasProposedCurrent &&
    !hasActualPrev1 &&
    approvedByYear.has(prev1) &&
    actualByYear.has(prev2) &&
    actualByYear.has(prev3);

  // Special 4-column structure (either proposed-only OR forward-approved-only)
  const isSpecial4ColMode = isCurrentYearProposedOnly || isForwardApprovedOnly;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Gets the source row for a given year from a year-indexed map.
   *
   * @param {Map<number, ProgramYearRow>} map
   * @param {number} year
   * @returns {ProgramYearRow|null}
   */
  const getRow = (map, year) => map.get(year) || null;

  /**
   * Retrieves a numeric value for `key` from the year row, otherwise null.
   *
   * @param {Map<number, ProgramYearRow>} map
   * @param {number} year
   * @param {string} key
   * @returns {number|null}
   */
  const getVal = (map, year, key) => {
    const row = map.get(year);
    const raw = row ? row[key] : null;
    return typeof raw === "number" ? raw : null;
  };

  /**
   * Simplifies labels for snapshot display by removing common prefixes.
   *
   * @param {string} [label=""]
   * @returns {string}
   */
  const simplifyLabel = (label = "") =>
    label
      .replace(/^Positions\s+–\s*/i, "")
      .replace(/^Salaries\s+–\s*/i, "")
      .trim();

  /**
   * Determines the canonical ordering of field keys in the snapshot.
   *
   * Primary ordering source:
   * - `tables[].fields` from the "preferred" table:
   *   - approvedMain
   *   - sectionKey Approved
   *   - actualMain
   *   - sectionKey Actual
   *   - otherwise tables[0]
   *
   * Fallback ordering:
   * - fieldDefs order, if no `fields` exist on the preferred table
   *
   * @param {Array<Object>} tables
   * @param {FieldDef[]} fieldDefs
   * @returns {string[]} Deduped field keys preserving the chosen ordering.
   */
  function getCanonicalFieldOrder(tables = [], fieldDefs = []) {
    if (!Array.isArray(tables)) tables = [];

    const preferred =
      tables.find((t) => t?.id === "approvedMain") ||
      tables.find((t) => t?.sectionKey === "Approved") ||
      tables.find((t) => t?.id === "actualMain") ||
      tables.find((t) => t?.sectionKey === "Actual") ||
      tables[0];

    const fromTables = Array.isArray(preferred?.fields) ? preferred.fields : [];

    // If you want to hard-fail instead of fallback, flip this to true.
    const STRICT_SNAPSHOT_FIELDS = false;

    if (!fromTables.length) {
      const prefLabel =
        preferred?.id || preferred?.title || preferred?.sectionKey || "unknown";

      const msg =
        `Snapshot ordering requires tables[].fields (Option A), ` +
        `but no fields were found on preferred table (${prefLabel}).`;

      if (STRICT_SNAPSHOT_FIELDS) {
        console.warn(`⚠️ ${msg} Returning empty snapshot.`);
        return [];
      }

      console.warn(`⚠️ ${msg} Falling back to fieldDefs order for now.`);

      const fromFieldDefs = Array.isArray(fieldDefs)
        ? fieldDefs.map((fd) => fd?.key).filter(Boolean)
        : [];

      return dedupePreserveOrder(fromFieldDefs);
    }

    return dedupePreserveOrder(fromTables);
  }

  /**
   * Removes duplicates while preserving the first-seen order.
   *
   * @param {string[]} keys
   * @returns {string[]}
   */
  function dedupePreserveOrder(keys) {
    const seen = new Set();
    return (Array.isArray(keys) ? keys : []).filter((k) => {
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }

  /**
   * Produces a cell renderer that formats numeric values:
   * - Dash for null/undefined
   * - Percent via `formatPercent` when isPercent = true
   * - Currency formatting for currency rows (with parentheses for negatives)
   * - Otherwise numeric display with up to 2 decimals (trim ".00")
   *
   * Also applies:
   * - red text for negatives
   * - bold font for subtotal/total rows
   *
   * @param {string} colKey
   * @param {boolean} [isPercent=false]
   * @returns {(row: SnapshotRow) => JSX.Element}
   */
  const makeNumberRenderer =
    (colKey, isPercent = false) =>
    (row) => {
      const val = row[colKey];
      if (val == null) return <div className="text-right">—</div>;

      const isNegative = typeof val === "number" && val < 0;
      const baseClass =
        "text-right " +
        (isNegative ? "text-red-600 " : "") +
        (row.rowType === "subtotal" || row.rowType === "total"
          ? "font-semibold"
          : "");

      let formatted;

      if (isPercent) {
        formatted = formatPercent(val);
      } else if (row.valueType === "currency") {
        formatted =
          val < 0 ? `(${formatCurrency(Math.abs(val))})` : formatCurrency(val);
      } else {
        formatted =
          typeof val === "number"
            ? String(val % 1 === 0 ? val : val.toFixed(2)).replace(/\.00$/, "")
            : val;
      }

      return <div className={baseClass}>{formatted}</div>;
    };

  // ---------------------------------------------------------------------------
  // Offset rule support
  // ---------------------------------------------------------------------------

  /**
   * Retrieves the offset rules object from a year-row, supporting several field names.
   *
   * @param {ProgramYearRow} row
   * @returns {Object<string, OffsetRule>|Object<string, any>}
   */
  const getRules = (row) =>
    row?._offsetRules || row?.OffsetRules || row?.offsetRules || {};

  /**
   * Normalizes a field key into a likely offset-rule lookup key.
   * Includes special casing and best-effort Title Case conversion.
   *
   * @param {string} [fieldKey=""]
   * @returns {string}
   */
  const resolveRuleKey = (fieldKey = "") => {
    const fk = String(fieldKey);

    // Very common case
    if (/grant/i.test(fk)) return "Grant";

    // Strip common prefixes: offsetsGrant, offsetGrant, Offsets_Grant, etc.
    const stripped = fk
      .replace(/^offsets?/i, "")
      .replace(/^[\s_\-–—:]+/, "")
      .trim();

    if (stripped) {
      const words = stripped
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_\-–—:]+/g, " ")
        .trim();

      if (/^grant$/i.test(words)) return "Grant";

      // Best-effort Title Case for rule map lookup
      return words.charAt(0).toUpperCase() + words.slice(1);
    }

    return fk;
  };

  /**
   * Determines whether a particular field should be excluded from totals
   * based on the row's offset-rule metadata.
   *
   * Lookup order:
   * 1) direct rules[fieldKey]
   * 2) normalized rules[resolveRuleKey(fieldKey)]
   *
   * @param {ProgramYearRow} row
   * @param {string} fieldKey
   * @returns {boolean}
   */
  const isExcludedFromTotals = (row, fieldKey) => {
    const rules = getRules(row);
    if (!rules) return false;

    // Direct match first
    const direct = rules?.[fieldKey];
    if (direct && direct.countInTotals === false) return true;

    // Then normalized match
    const ruleKey = resolveRuleKey(fieldKey);
    const normalized = rules?.[ruleKey];
    return !!(normalized && normalized.countInTotals === false);
  };

  /**
   * Retrieves a note explaining offset exclusion, if present.
   *
   * Lookup order:
   * 1) rules[fieldKey].note
   * 2) rules[resolveRuleKey(fieldKey)].note
   *
   * @param {ProgramYearRow} row
   * @param {string} fieldKey
   * @returns {string|null}
   */
  const getOffsetRuleNote = (row, fieldKey) => {
    const rules = getRules(row);
    if (!rules) return null;

    const direct = rules?.[fieldKey]?.note;
    if (typeof direct === "string" && direct.trim()) return direct.trim();

    const ruleKey = resolveRuleKey(fieldKey);
    const normalized = rules?.[ruleKey]?.note;
    return typeof normalized === "string" && normalized.trim()
      ? normalized.trim()
      : null;
  };

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------

  /**
   * Current year column label:
   * Prefer Approved if present, otherwise Proposed if present.
   * Defaults to Approved label when neither is explicitly found (fallback).
   *
   * @type {string}
   */
  const currentColLabel = hasApprovedCurrent
    ? `${formatFiscalYearFromStart(current)} Approved`
    : hasProposedCurrent
      ? `${formatFiscalYearFromStart(current)} Proposed`
      : `${formatFiscalYearFromStart(current)} Approved`;

  /** @type {SnapshotColumn[]} */
  const snapshotColumns = [
    {
      key: "label",
      label: "",
      style: { width: "260px", minWidth: "260px" },
      align: "left",
      editable: false,
    },

    ...(isSpecial4ColMode
      ? [
          {
            key: "y3Actual",
            label: `${formatFiscalYearFromStart(prev3)} Actual`,
            style: { width: "110px", minWidth: "110px" },
            align: "right",
            render: makeNumberRenderer("y3Actual"),
          },
          {
            key: "y2Actual",
            label: `${formatFiscalYearFromStart(prev2)} Actual`,
            style: { width: "110px", minWidth: "110px" },
            align: "right",
            render: makeNumberRenderer("y2Actual"),
          },
          {
            key: "y1Approved",
            label: `${formatFiscalYearFromStart(prev1)} Approved`,
            style: { width: "110px", minWidth: "110px" },
            align: "right",
            render: makeNumberRenderer("y1Approved"),
          },
        ]
      : [
          {
            key: "y2Actual",
            label: `${formatFiscalYearFromStart(prev2)} Actual`,
            style: { width: "110px", minWidth: "110px" },
            align: "right",
            render: makeNumberRenderer("y2Actual"),
          },
          {
            key: "y1Approved",
            label: `${formatFiscalYearFromStart(prev1)} Approved`,
            style: { width: "125px", minWidth: "125px" },
            align: "right",
            render: makeNumberRenderer("y1Approved"),
          },
          {
            key: "y1Actual",
            label: `${formatFiscalYearFromStart(prev1)} Actual`,
            style: { width: "110px", minWidth: "110px" },
            align: "right",
            render: makeNumberRenderer("y1Actual"),
          },
        ]),

    {
      key: "y0Approved",
      label: currentColLabel,
      style: {
        width: isSpecial4ColMode ? "110px" : "125px",
        minWidth: isSpecial4ColMode ? "110px" : "125px",
      },
      align: "right",
      render: makeNumberRenderer("y0Approved"),
    },
    {
      key: "change",
      label: "Change",
      style: { width: "100px", minWidth: "100px" },
      align: "right",
      render: makeNumberRenderer("change"),
    },
    {
      key: "percentChange",
      label: "% Change",
      style: { width: "100px", minWidth: "100px" },
      align: "right",
      render: makeNumberRenderer("percentChange", true),
    },
  ];

  /**
   * Aggregation keys depend on mode (normal mode has y1Actual; 4-col does not).
   *
   * @type {Array<"y3Actual"|"y2Actual"|"y1Approved"|"y1Actual"|"y0Approved">}
   */
  const yearValueKeys = isSpecial4ColMode
    ? ["y3Actual", "y2Actual", "y1Approved", "y0Approved"]
    : ["y2Actual", "y1Approved", "y1Actual", "y0Approved"];

  // ---------------------------------------------------------------------------
  // Change / %Change baseline rules
  // ---------------------------------------------------------------------------

  /**
   * Determines which column is used as the baseline for Change/%Change.
   *
   * Baseline:
   * - Normal mode: (Y-1) Actual when current approved exists
   * - Special 4-col modes: (Y-1) Approved
   *
   * @returns {"y1Actual"|"y1Approved"}
   */
  const getBaselineKey = () => {
    // Normal mode: baseline is (Y-1) Actual
    if (!isSpecial4ColMode && hasApprovedCurrent) return "y1Actual";

    // Special 4-col modes (proposed-only OR forward-approved-only):
    // baseline is (Y-1) Approved
    if (isSpecial4ColMode) return "y1Approved";

    // Fallback
    return "y1Actual";
  };

  const baselineKey = getBaselineKey();

  /**
   * Computes derived Change and % Change.
   * - If either side is null: returns nulls
   * - Baseline 0: only define pct when change is also 0
   * - pct value is stored as "percent number" (e.g. 12.34 means 12.34%)
   *
   * @param {number|null} currentVal
   * @param {number|null} baselineVal
   * @returns {{change: number|null, pct: number|null}}
   */
  const computeChangeAndPct = (currentVal, baselineVal) => {
    // If either side is missing, no change/pct
    if (currentVal == null || baselineVal == null) {
      return { change: null, pct: null };
    }

    const change = currentVal - baselineVal;

    // Baseline 0: only define pct when change is also 0
    if (baselineVal === 0) {
      return { change, pct: change === 0 ? 0 : null };
    }

    // formatPercent expects a fraction (0.12 => 12%) OR percent (12 => 12%)?
    // In your app, buildSnapshot uses pct as a percent number. Keep it consistent.
    return { change, pct: (change / baselineVal) * 100 };
  };

  // ---------------------------------------------------------------------------
  // Row generation
  // ---------------------------------------------------------------------------

  /** @type {SnapshotRow[]} */
  const snapshotRows = [];
  /** @type {SnapshotNote[]} */
  const snapshotNotes = [];
  const noteDedup = new Set();

  /**
   * Collects a deduped explanatory note for fields excluded from totals.
   *
   * @param {ProgramYearRow|null} rowObj
   * @param {string} fieldKey
   * @returns {void}
   */
  const maybeCollectNote = (rowObj, fieldKey) => {
    if (!rowObj) return;
    if (!isExcludedFromTotals(rowObj, fieldKey)) return;

    const note =
      getOffsetRuleNote(rowObj, fieldKey) ||
      "Shown for reference; not applied to Budget Total in source document.";

    const dedupKey = `${resolveRuleKey(fieldKey)}::${note}`;
    if (noteDedup.has(dedupKey)) return;
    noteDedup.add(dedupKey);

    snapshotNotes.push({ key: resolveRuleKey(fieldKey), note });
  };

  let currentGroupKey = null;

  /**
   * Creates a totals accumulator with all year keys initialized to 0.
   *
   * @returns {Record<string, number>}
   */
  const makeTotalsObj = () =>
    yearValueKeys.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});

  let salariesTotals = makeTotalsObj();
  let salariesHasCurrency = false;

  const programTotals = makeTotalsObj();

  /**
   * Adds a numeric value into an accumulator only if:
   * - val is numeric
   * - rowObj/fieldKey is not excluded from totals by offset rules
   *
   * @param {Record<string, number>} target
   * @param {string} yearKey
   * @param {ProgramYearRow|null} rowObj
   * @param {string} fieldKey
   * @param {number|null} val
   * @returns {void}
   */
  const addIfCounted = (target, yearKey, rowObj, fieldKey, val) => {
    if (typeof val !== "number") return;
    if (isExcludedFromTotals(rowObj, fieldKey)) return;
    target[yearKey] += val;
  };

  /**
   * Pushes a "Subtotal" row for Salaries, when applicable, and resets the accumulator.
   *
   * @param {boolean} [force=false]
   * @returns {void}
   */
  const pushSalariesSubtotalIfNeeded = (force = false) => {
    if (!force) return;
    if (!salariesHasCurrency) return;

    const baselineSubtotal =
      baselineKey === "y1Actual"
        ? salariesTotals.y1Actual
        : salariesTotals.y1Approved;

    const { change, pct } = computeChangeAndPct(
      salariesTotals.y0Approved,
      baselineSubtotal
    );

    snapshotRows.push({
      rowType: "subtotal",
      label: "Subtotal",
      valueType: "currency",
      ...yearValueKeys.reduce((acc, k) => {
        acc[k] = salariesTotals[k] || null;
        return acc;
      }, {}),
      change,
      percentChange: pct,
    });

    salariesTotals = makeTotalsObj();
    salariesHasCurrency = false;
  };

  for (const fd of orderedFieldDefs) {
    const fieldKey = fd.key;
    const groupKey = fd.group || "other";

    if (currentGroupKey && groupKey !== currentGroupKey) {
      if (currentGroupKey === "salaries") pushSalariesSubtotalIfNeeded(true);
    }

    if (groupKey !== currentGroupKey) {
      currentGroupKey = groupKey;
      snapshotRows.push({ section: groupLabelFromKey(groupKey) });
    }

    // Row objects for rule lookups
    const rowPrev3A = isSpecial4ColMode ? getRow(actualByYear, prev3) : null;
    const rowPrev2A = getRow(actualByYear, prev2);
    const rowPrev1Ap = getRow(approvedByYear, prev1);
    const rowPrev1A = !isSpecial4ColMode ? getRow(actualByYear, prev1) : null;
    const rowCurrAp = getRow(approvedByYear, current);
    const rowCurrProp = getRow(proposedByYear, current);

    // Values for display
    const v3 = isSpecial4ColMode ? getVal(actualByYear, prev3, fieldKey) : null;
    const v2 = getVal(actualByYear, prev2, fieldKey);

    const v1Approved = getVal(approvedByYear, prev1, fieldKey);
    const v1Actual = !isSpecial4ColMode
      ? getVal(actualByYear, prev1, fieldKey)
      : null;

    const v0Approved = getVal(approvedByYear, current, fieldKey);
    const v0Proposed = getVal(proposedByYear, current, fieldKey);

    // Current value is Approved if present, else Proposed if present
    const v0Current = v0Approved != null ? v0Approved : v0Proposed;

    // Which row provided current value? (for offset rule lookup)
    const rowCurr = v0Approved != null ? rowCurrAp : rowCurrProp;

    const hasAny =
      (isSpecial4ColMode ? v3 != null : false) ||
      v2 != null ||
      v1Approved != null ||
      (!isSpecial4ColMode ? v1Actual != null : false) ||
      v0Current != null;

    if (!hasAny) continue;

    // Determine if excluded in any displayed year
    const anyExcluded =
      (isSpecial4ColMode &&
        rowPrev3A &&
        isExcludedFromTotals(rowPrev3A, fieldKey)) ||
      (rowPrev2A && isExcludedFromTotals(rowPrev2A, fieldKey)) ||
      (rowPrev1Ap && isExcludedFromTotals(rowPrev1Ap, fieldKey)) ||
      (!isSpecial4ColMode &&
        rowPrev1A &&
        isExcludedFromTotals(rowPrev1A, fieldKey)) ||
      (rowCurr && isExcludedFromTotals(rowCurr, fieldKey));

    // Collect notes from any year where excluded
    if (anyExcluded) {
      maybeCollectNote(rowCurr, fieldKey);
      maybeCollectNote(rowPrev1Ap, fieldKey);
      maybeCollectNote(rowPrev1A, fieldKey);
      maybeCollectNote(rowPrev2A, fieldKey);
      maybeCollectNote(rowPrev3A, fieldKey);
    }

    const baselineVal = baselineKey === "y1Actual" ? v1Actual : v1Approved;
    const { change, pct } = computeChangeAndPct(v0Current, baselineVal);

    const labelBase = simplifyLabel(fd.label);

    const row = {
      rowType: "detail",
      order: orderCounter++,
      label: anyExcluded ? `${labelBase} *` : labelBase,
      valueType: fd.valueType,
      group: groupKey,

      y3Actual: v3,
      y2Actual: v2,
      y1Approved: v1Approved,
      y1Actual: v1Actual,
      y0Approved: v0Current,

      change,
      percentChange: pct,
    };

    snapshotRows.push(row);

    if (fd.valueType === "currency") {
      // Salaries subtotal (skip excluded offsets)
      if (groupKey === "salaries") {
        if (isSpecial4ColMode) {
          addIfCounted(salariesTotals, "y3Actual", rowPrev3A, fieldKey, v3);
        }
        addIfCounted(salariesTotals, "y2Actual", rowPrev2A, fieldKey, v2);
        addIfCounted(
          salariesTotals,
          "y1Approved",
          rowPrev1Ap,
          fieldKey,
          v1Approved
        );
        if (!isSpecial4ColMode) {
          addIfCounted(
            salariesTotals,
            "y1Actual",
            rowPrev1A,
            fieldKey,
            v1Actual
          );
        }
        addIfCounted(salariesTotals, "y0Approved", rowCurr, fieldKey, v0Current);
        salariesHasCurrency = true;
      }

      // Program totals (skip excluded offsets)
      if (isSpecial4ColMode) {
        addIfCounted(programTotals, "y3Actual", rowPrev3A, fieldKey, v3);
      }
      addIfCounted(programTotals, "y2Actual", rowPrev2A, fieldKey, v2);
      addIfCounted(
        programTotals,
        "y1Approved",
        rowPrev1Ap,
        fieldKey,
        v1Approved
      );
      if (!isSpecial4ColMode) {
        addIfCounted(programTotals, "y1Actual", rowPrev1A, fieldKey, v1Actual);
      }
      addIfCounted(programTotals, "y0Approved", rowCurr, fieldKey, v0Current);
    }
  }

  if (currentGroupKey === "salaries") {
    pushSalariesSubtotalIfNeeded(true);
  }

  const anyTotal = yearValueKeys.some((k) => programTotals[k] !== 0);

  if (anyTotal) {
    const baselineTotal =
      baselineKey === "y1Actual"
        ? programTotals.y1Actual
        : programTotals.y1Approved;

    const { change: totalChange, pct: totalPct } = computeChangeAndPct(
      programTotals.y0Approved,
      baselineTotal
    );

    const totalLabel = programLabel ? `TOTAL ${programLabel}` : "TOTAL";

    snapshotRows.push({
      rowType: "total",
      isTotalRow: true,
      label: totalLabel,
      valueType: "currency",
      ...programTotals,
      change: totalChange,
      percentChange: totalPct,
    });
  }

  return { snapshotColumns, snapshotRows, snapshotNotes };
}

/**
 * groupLabelFromKey
 *
 * Maps a normalized group key from FieldDef.group into a snapshot section header label.
 *
 * @param {string} key
 * @returns {string}
 */
function groupLabelFromKey(key) {
  if (!key) return "OTHER";
  switch (key) {
    case "positions":
      return "POSITIONS (FTE)";
    case "salaries":
      return "SALARIES";
    case "materials":
      return "SUPPLIES/MATERIALS";
    case "offsets":
      return "OFFSETS";
    case "other":
      return "OTHER";
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
}
